/**
 * The pure half of the transfer notice (owner, 2026-10-31): what the form still needs, which
 * accounts it offers and how a notice's status reads. The server decides; this only keeps the
 * button honest and the words short.
 */
import type { ClientBankAccount } from '../../services/bankAccounts.service';
import type { DepositNoticeStatus } from '../../services/depositNotices.service';

export interface NoticeSeries {
  product_id: string;
  product_name: string;
  currency_code: string | null;
}

export interface NoticeDraft {
  productId: string;
  accountId: string;
  amount: string;
  sentOn: string;
}

export const EMPTY_NOTICE: NoticeDraft = { productId: '', accountId: '', amount: '', sentOn: '' };

export const NOTICE_STATUS_LABEL: Record<DepositNoticeStatus, string> = {
  open: "We're looking for it",
  matched: 'Received',
  past: 'Our team is reviewing it',
};

/** The calendar day of `now` in the reader's own zone, as YYYY-MM-DD. */
export function localDay(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** The registered accounts a transfer for this series can have left: active, same currency. */
export function accountsFor(
  accounts: readonly ClientBankAccount[],
  series: NoticeSeries | undefined,
): ClientBankAccount[] {
  if (!series?.currency_code) return [];
  return accounts.filter((a) => a.is_active && a.currency_code === series.currency_code);
}

/** What the form still needs, or null when it can be sent. */
export function noticeProblem(draft: NoticeDraft, today: string): string | null {
  if (!draft.productId) return 'Choose the series.';
  if (!draft.accountId) return 'Choose the account you sent it from.';
  const amount = Number(draft.amount);
  if (!draft.amount || !Number.isFinite(amount) || amount <= 0) return 'Enter the amount you sent.';
  if (!draft.sentOn) return 'Choose the day you sent it.';
  if (draft.sentOn > today) return 'The day you sent it cannot be in the future.';
  return null;
}

/** How an account reads in the picker: its nickname, or its type, and its last four digits. */
export function accountLabel(account: ClientBankAccount): string {
  const name = account.nickname?.trim() || account.account_type;
  return `${name} ···· ${account.account_number_last4}`;
}
